import { toast } from '@/hooks/use-toast';

/**
 * Standardized error handling system for the Behtari platform
 * Phase 1: Critical Error Handling Fixes
 */

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  metadata?: Record<string, any>;
}

export interface ErrorReport {
  error: Error;
  context: ErrorContext;
  timestamp: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

class ErrorTracker {
  private errors: ErrorReport[] = [];
  private maxErrors = 100;

  logError(error: Error, context: ErrorContext = {}, severity: ErrorReport['severity'] = 'medium') {
    const report: ErrorReport = {
      error,
      context,
      timestamp: Date.now(),
      severity
    };

    this.errors.unshift(report);
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // In development, log to console
    if (import.meta.env.DEV) {
      console.error(`[${severity.toUpperCase()}] Error in ${context.component || 'Unknown'}:`, error);
      if (context.metadata) {
        console.error('Metadata:', context.metadata);
      }
    }

    // In production, send to monitoring service
    if (import.meta.env.PROD && severity === 'critical') {
      // Production error reporting with retry logic
      this.reportToService(report);
    }
  }

  private reportToService(report: ErrorReport) {
    // Placeholder for production error reporting
    fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(report)
    }).catch(() => {
      // Silently fail error reporting to avoid error loops
    });
  }

  getRecentErrors() {
    return this.errors.slice();
  }

  clearErrors() {
    this.errors = [];
  }
}

export const errorTracker = new ErrorTracker();

// Standardized error handling functions
export const handleError = (
  error: Error,
  context: ErrorContext = {},
  showToast = true
) => {
  const severity = determineSeverity(error);
  
  errorTracker.logError(error, context, severity);

  if (showToast) {
    showErrorToast(error, context);
  }
};

export const handleAsyncError = async <T>(
  promise: Promise<T>,
  context: ErrorContext = {}
): Promise<T | null> => {
  try {
    return await promise;
  } catch (error) {
    handleError(error as Error, context);
    return null;
  }
};

export const safeExecute = <T extends (...args: any[]) => any>(
  fn: T,
  context: ErrorContext = {},
  fallback?: ReturnType<T>
): (...args: Parameters<T>) => ReturnType<T> | typeof fallback => {
  return (...args: Parameters<T>) => {
    try {
      return fn(...args);
    } catch (error) {
      handleError(error as Error, context);
      return fallback as ReturnType<T>;
    }
  };
};

// Error severity determination
const determineSeverity = (error: Error): ErrorReport['severity'] => {
  const message = error.message.toLowerCase();
  
  if (message.includes('network') || message.includes('fetch')) {
    return 'medium';
  }
  
  if (message.includes('auth') || message.includes('permission')) {
    return 'high';
  }
  
  if (message.includes('critical') || message.includes('system')) {
    return 'critical';
  }
  
  return 'low';
};

// Toast notifications for errors
const showErrorToast = (error: Error, context: ErrorContext) => {
  const userFriendlyMessage = getUserFriendlyMessage(error, context);
  
  toast({
    title: "Something went wrong",
    description: userFriendlyMessage,
    variant: "destructive",
  });
};

const getUserFriendlyMessage = (error: Error, context: ErrorContext): string => {
  const { action, component } = context;
  
  // Network errors
  if (error.message.includes('fetch') || error.message.includes('network')) {
    return "Please check your internet connection and try again.";
  }
  
  // Authentication errors
  if (error.message.includes('auth') || error.message.includes('unauthorized')) {
    return "Please log in again to continue.";
  }
  
  // Validation errors
  if (error.message.includes('validation') || error.message.includes('invalid')) {
    return "Please check your input and try again.";
  }
  
  // Context-specific messages
  if (action) {
    return `Unable to ${action}. Please try again.`;
  }
  
  return "An unexpected error occurred. Please try again.";
};

// React error boundary integration
export const createErrorBoundaryHandler = (component: string) => {
  return (error: Error, errorInfo: any) => {
    handleError(error, {
      component,
      metadata: { errorInfo }
    }, false); // Don't show toast in error boundaries
  };
};

// Async operation wrapper with error handling
export const withErrorHandling = <T extends (...args: any[]) => Promise<any>>(
  asyncFn: T,
  context: ErrorContext = {}
): T => {
  return (async (...args: Parameters<T>) => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      handleError(error as Error, context);
      throw error; // Re-throw for component handling
    }
  }) as T;
};

// Error handler instance for comprehensive error boundary
export const errorHandler = {
  trackError: (error: Error, context?: Record<string, any>) => {
    handleError(error, context || {});
  }
};